import { useEffect, useState } from 'react';
import { Plus, ArrowUp, ArrowDown } from 'lucide-react';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Switch } from '../../../components/ui/switch';
import { Button } from '../../../components/ui/button';
import {
  adminList, adminCreate, adminUpdate, adminDelete, adminReorder,
} from '../../../lib/adminApi';
import { SectionHeader, SaveBar, useSaver, ConfirmDelete, Field, Card } from './_ui';
import ImagePicker from './ImagePicker';

function toLines(arr) { return (arr || []).join('\n'); }
function fromLines(str) {
  return String(str || '').split('\n').map((s) => s.trim()).filter(Boolean);
}

export default function CoursesSection() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setCourses(await adminList('courses'));
    setLoading(false);
  };
  useEffect(() => { refresh(); }, []);

  const addCourse = async () => {
    await adminCreate('courses', {
      title: 'New course', subtitle: '', level: 'GCSE',
      yearGroup: '', body: '',
      boards: [], tags: [], highlights: [],
      lessonFrequency: 'Weekly · 1 hour',
      price: { headline: '', subline: '' },
      exploreLabel: 'View course', image: '',
      active: true, order: courses.length,
    });
    await refresh();
  };

  const move = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= courses.length) return;
    const next = [...courses];
    [next[i], next[j]] = [next[j], next[i]];
    setCourses(next);
    await adminReorder('courses', next.map((c) => c.id));
  };

  return (
    <div className="max-w-5xl">
      <SectionHeader
        title="Courses & Pricing"
        description="Prices, bullet points, exam boards, year groups and active status per course."
        actions={
          <Button
            onClick={addCourse}
            className="bg-brand-sun text-brand-ink hover:brightness-95 rounded-full"
            data-testid="add-course"
          >
            <Plus className="w-4 h-4 mr-1" /> Add course
          </Button>
        }
      />

      {loading ? (
        <p className="text-brand-ink/60">Loading...</p>
      ) : (
        <div className="space-y-4">
          {courses.map((c, i) => (
            <CourseRow
              key={c.id}
              index={i}
              total={courses.length}
              course={c}
              onChange={(next) =>
                setCourses((prev) => prev.map((x) => (x.id === c.id ? { ...x, ...next } : x)))
              }
              onSave={async (next) => { await adminUpdate('courses', c.id, next); }}
              onDelete={async () => { await adminDelete('courses', c.id); await refresh(); }}
              onMoveUp={() => move(i, -1)}
              onMoveDown={() => move(i, 1)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function CourseRow({ course, index, total, onChange, onSave, onDelete, onMoveUp, onMoveDown }) {
  const saver = useSaver();
  const c = course;
  const price = c.price || { headline: '', subline: '' };
  return (
    <Card>
      <div className="flex items-start gap-3">
        <div className="flex flex-col shrink-0">
          <button type="button" onClick={onMoveUp} disabled={index === 0}
                  className="p-1 rounded-md hover:bg-brand-ink/5 disabled:opacity-30" aria-label="Move up">
            <ArrowUp className="w-4 h-4" />
          </button>
          <button type="button" onClick={onMoveDown} disabled={index === total - 1}
                  className="p-1 rounded-md hover:bg-brand-ink/5 disabled:opacity-30" aria-label="Move down">
            <ArrowDown className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 min-w-0 grid md:grid-cols-2 gap-3">
          <Field label="Title">
            <Input value={c.title || ''} onChange={(e) => onChange({ title: e.target.value })} />
          </Field>
          <Field label="Subtitle">
            <Input value={c.subtitle || ''} onChange={(e) => onChange({ subtitle: e.target.value })} />
          </Field>
          <Field label="Level">
            <Input value={c.level || ''} onChange={(e) => onChange({ level: e.target.value })} />
          </Field>
          <Field label="Year group">
            <Input value={c.yearGroup || ''} onChange={(e) => onChange({ yearGroup: e.target.value })} />
          </Field>
          <Field label="Exam boards" hint="comma separated" cols={2}>
            <Input
              value={(c.boards || []).join(', ')}
              onChange={(e) => onChange({
                boards: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
              })}
            />
          </Field>
          <Field label="Description" cols={2}>
            <Textarea rows={2} value={c.body || ''} onChange={(e) => onChange({ body: e.target.value })} />
          </Field>
          <Field label="Bullet points (one per line)" cols={2}>
            <Textarea
              rows={5}
              value={toLines(c.highlights)}
              onChange={(e) => onChange({ highlights: fromLines(e.target.value) })}
            />
          </Field>
          <Field label="Price headline" hint='e.g. "£19/hour"'>
            <Input
              value={price.headline || ''}
              onChange={(e) => onChange({ price: { ...price, headline: e.target.value } })}
            />
          </Field>
          <Field label="Price subline">
            <Input
              value={price.subline || ''}
              onChange={(e) => onChange({ price: { ...price, subline: e.target.value } })}
            />
          </Field>
          <Field label="Lesson frequency">
            <Input
              value={c.lessonFrequency || ''}
              onChange={(e) => onChange({ lessonFrequency: e.target.value })}
            />
          </Field>
          <Field label="Explore-label CTA">
            <Input
              value={c.exploreLabel || ''}
              onChange={(e) => onChange({ exploreLabel: e.target.value })}
            />
          </Field>

          <div className="md:col-span-2">
            <ImagePicker
              label="Card image"
              value={c.image}
              onChange={(url) => onChange({ image: url })}
              size="lg"
            />
          </div>

          <div className="md:col-span-2 flex items-center gap-6">
            <label className="inline-flex items-center gap-2 text-sm">
              <Switch
                checked={c.active !== false}
                onCheckedChange={(v) => onChange({ active: v })}
              />
              Active / visible
            </label>
          </div>

          <div className="md:col-span-2 flex items-center justify-between pt-2 border-t border-brand-ink/10 mt-2">
            <ConfirmDelete onConfirm={onDelete} />
            <SaveBar {...saver} onSave={() => saver.run(() => onSave(c))} dataTestId={`save-course-${index}`} />
          </div>
        </div>
      </div>
    </Card>
  );
}
